import { useEffect, useState } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { SxStyle } from '../../../types';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import Box from '@mui/material/Box';
import firebaseApp from '../../../firebase-app';
import {
  LogDataGridFormat,
  convertFirestoreLogToDatagridFormat,
} from '../push-dashboard/utils/data';
import { getNotificationsLogPath } from '../../../utils/paths';
import { Match } from './utils/data';
import { PushNotificationLog } from '../push-dashboard/types';

const styles: SxStyle = {
  container: {
    width: '100%',
    height: 'calc(100vh - 300px)',
  },
  dataGrid: (theme) => ({
    border: 0,
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

async function getNotifications(type: string, abbrv: string, matchId: number) {
  const colRef = collection(firebaseApp.db, getNotificationsLogPath(type, abbrv?.toUpperCase()));
  const docSnap = await getDocs(
    query(
      colRef,
      where('data.matchId', '==', matchId.toString()),
      limit(50),
      orderBy('timestamp', 'desc')
    )
  );
  const docsData = docSnap?.docs.map((doc) => {
    return {
      ...doc.data(),
      app: doc.ref.path.split('/')[5].toUpperCase(),
    };
  }) as PushNotificationLog[];

  return docsData?.map(convertFirestoreLogToDatagridFormat) || [];
}

export default function MatchPushNotifications({ matchDoc }: { matchDoc: Match }) {
  const { strings } = useStringsContext();

  const [notifications, setNotifications] = useState<LogDataGridFormat[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const getAllNotifications = async () => {
      setLoading(true);
      setNotifications([
        ...(await getNotifications('match-event', matchDoc.configLeagueName, matchDoc.id)),
        ...(await getNotifications(
          'match-event',
          matchDoc.homeTeamAbbrv.toLowerCase(),
          matchDoc.id
        )),
        ...(await getNotifications(
          'match-event',
          matchDoc.visitingTeamAbbrv.toLowerCase(),
          matchDoc.id
        )),
      ]);
      setLoading(false);
    };
    getAllNotifications();
  }, [matchDoc]);

  const columns: GridColDef[] = [
    { field: 'timestamp', headerName: strings.pushNotifications.fieldNameDate, width: 160 },
    { field: 'app', headerName: strings.pushNotifications.fieldNameApp, width: 150 },
    { field: 'title', headerName: strings.pushNotifications.fieldNameTitle, width: 400 },
    { field: 'message', headerName: strings.pushNotifications.fieldNameMessage, flex: 1 },
  ];

  return (
    <Box sx={styles.container}>
      <DataGrid
        sx={styles.dataGrid}
        rows={notifications}
        columns={columns}
        loading={loading}
        autoPageSize
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'timestamp',
                sort: 'desc',
              },
            ],
          },
        }}
      />
    </Box>
  );
}
